<template>
  <div
    class="modal"
    :class="{ 'is-active': isActive }"
    @keyup.esc="$emit('close-modal')"
  >
    <div class="modal-background"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <div>
          <p class="modal-card-title">{{ modalTitle }}</p>
        </div>
      </header>
      <section class="modal-card-body" v-if="webhook">
        <div class="config-fields">
          <div class="field columns">
            <FormLabel
              title="URL"
              :has-tooltip="true"
              tooltip-placement="tooltip-bottom"
              :tooltip-text="$t('webhooks.tooltips.url')"
            />
            <div class="control column is-9 is-flex">
              <div class="input-container">{{ webhook.config.url }}</div>
            </div>
          </div>

          <div class="field columns gray-bg" v-if="webhook.config.auth">
            <FormLabel
              title="Username"
              :has-tooltip="true"
              :tooltip-text="$t('webhooks.tooltips.username')"
            />
            <div class="control column is-9 is-flex">
              <div class="input-container">
                {{ webhook.config.auth.username }}
              </div>
            </div>
          </div>

          <div class="field columns gray-bg" v-if="webhook.config.auth">
            <FormLabel
              :title="$t('register_form.password_label')"
              :has-tooltip="true"
              :tooltip-text="$t('webhooks.tooltips.password')"
            />
            <div class="control column is-9 is-flex">
              <div class="input-container">*obfuscated*</div>
            </div>
          </div>

          <div class="field columns gray-bg" v-if="webhook.config.oauth2_client_credentials">
            <FormLabel
              :title="$t('webhooks.form_labels.oauth2_client_credentials.client_id')"
              :has-tooltip="true"
              :tooltip-text="$t('webhooks.tooltips.oauth2_client_credentials.client_id')"
            />
            <div class="control column is-9 is-flex">
              <div class="input-container">
                {{ webhook.config.oauth2_client_credentials.client_id }}
              </div>
            </div>
          </div>

          <div class="field columns gray-bg" v-if="webhook.config.oauth2_client_credentials">
            <FormLabel
              :title="$t('webhooks.form_labels.oauth2_client_credentials.client_secret')"
              :has-tooltip="true"
              :tooltip-text="$t('webhooks.tooltips.oauth2_client_credentials.client_secret')"
            />
            <div class="control column is-9 is-flex">
              <div class="input-container">*obfuscated*</div>
            </div>
          </div>

          <div class="field columns gray-bg" v-if="webhook.config.oauth2_client_credentials">
            <FormLabel
              :title="$t('webhooks.form_labels.oauth2_client_credentials.scope')"
              :has-tooltip="true"
              :tooltip-text="$t('webhooks.tooltips.oauth2_client_credentials.scope')"
            />
            <div class="control column is-9 is-flex">
              <div class="input-container">
                {{ webhook.config.oauth2_client_credentials.scope }}
              </div>
            </div>
          </div>

          <div class="field columns gray-bg" v-if="webhook.config.oauth2_client_credentials">
            <FormLabel
              :title="$t('webhooks.form_labels.oauth2_client_credentials.authorization_endpoint')"
              :has-tooltip="true"
              :tooltip-text="$t('webhooks.tooltips.oauth2_client_credentials.authorization_endpoint')"
            />
            <div class="control column is-9 is-flex">
              <div class="input-container">
                {{ webhook.config.oauth2_client_credentials.authorization_endpoint }}
              </div>
            </div>
          </div>

          <div class="field">
            <div class="columns">
              <FormLabel
                title="Selected events"
                :has-tooltip="true"
                :tooltip-text="$t('webhooks.tooltips.events')"
              />
            </div>
            <div class="columns">
              <div class="column is-3"></div>
              <div class="control column is-9">
                <div class="event-options-container">
                  <div
                    v-for="(event, index) in webhook.config.events"
                    :key="index"
                    class="event-container"
                  >
                    <div class="event-text">
                      {{ eventOption[event].label }}
                    </div>
                    <div class="tooltip-container">
                      <i class="material-icons">&#xE88F;</i>
                      <div class="tooltip tooltip-top" role="tooltip">
                        <div class="tooltip-outer">
                          <div class="tooltip-arrow"></div>
                          <div class="tooltip-inner">
                            {{ $t(`webhooks.tooltips.${event}`) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="field columns">
            <FormLabel
              title="Max payload size"
              :has-tooltip="true"
              :tooltip-text="$t('webhooks.tooltips.max_payload_size')"
            />
            <div class="control column is-9">
              <div class="input-container">
                {{ webhook.config.batching.max_payload_size }} bytes
              </div>
            </div>
          </div>
          <div class="field columns">
            <FormLabel
              title="Max wait time"
              :has-tooltip="true"
              :tooltip-text="$t('webhooks.tooltips.max_wait_time')"
            />
            <div class="control column is-9">
              <div class="input-container">
                {{ webhook.config.batching.max_wait_time }} seconds
              </div>
            </div>
          </div>
          <div class="field columns">
            <FormLabel
              title="Message TTL"
              :has-tooltip="true"
              :tooltip-text="$t('webhooks.tooltips.message_ttl')"
            />
            <div class="control column is-9">
              <div class="input-container">
                {{ webhook.config.message_ttl }} seconds
              </div>
            </div>
          </div>
        </div>

        <div class="modal-card-footer is-flex">
          <button
            type="button"
            class="button cancel-btn"
            @click="$emit('close-modal')"
          >
            Back
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import Ajv from 'ajv';
import { mapState, mapGetters } from 'vuex';
import { pick } from 'lodash';

import FormLabel from '../modules/FormLabel';
import notifications from 'services/Notifications';

export default {
  name: 'WebhookConfigViewModal',
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    modalTitle: {
      type: String,
      required: true,
    },
    webhook: {
      type: Object,
    },
  },
  components: {
    FormLabel,
  },
  data() {
    return {
      eventOption: {
        locations: {
          name: 'locations',
          label: 'Locations',
        },
        predictions: {
          name: 'predictions',
          label: 'Predictions',
        },
        stationaries: {
          name: 'stationaries',
          label: 'Stationaries',
        },
        moments: {
          name: 'moments',
          label: 'Moments',
        },
        all_transports: {
          name: 'all_transports',
          label: 'All Transports',
        },
        car_transports: {
          name: 'car_transports',
          label: 'Car Transports',
        },
        'x-stationary-nearby': {
          name: 'x-stationary-nearby',
          label: 'Nearby Stationary',
        },
        'x-stationary-covermore': {
          name: 'x-stationary-covermore',
          label: 'Covermore Stationary',
        },
        'x-nearby-location': {
          name: 'x-nearby-location',
          label: 'Nearby Location',
        },
        'x-stationary-significance': {
          name: 'x-stationary-significance',
          label: 'Stationary Significance',
        },
        approximate_location: {
          name: 'approximate_location',
          label: 'Approximate Location',
        },
      },
    };
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.modal-card-head,
.modal-card-footer {
  justify-content: center;
}

.modal-card {
  width: 800px;
}

.modal-card-body {
  padding: 0;
}

.modal-card-footer {
  padding-bottom: 3rem;
}

.gray-bg {
  background-color: $background;
}

.config-fields {
  padding: 1rem 0;
  margin-bottom: 2rem;
}

.event-options-container {
  display: flex;
  flex-wrap: wrap;
}

.event-container {
  display: flex;
  width: 50%;
  margin-bottom: 0.5rem;
  align-items: center;
}

.input-container {
  width: 90%;
  letter-spacing: 0.0625rem;
  font-size: 0.875rem;
}

.event-text {
  display: flex;
  width: 80%;
  border-radius: 0.25rem;
  background-color: $background;
  padding: 0.625rem 0.5rem;
  letter-spacing: 0.0625rem;
  font-size: 0.875rem;

  .icon {
    background-color: $black;
    font-size: 0.75rem;
    font-weight: bold;
  }
}

.tooltip-container {
  width: 20%;
  display: flex;

  .material-icons {
    font-size: 1.125rem;
    margin-left: 0.25rem;
  }
}

.field {
  padding: 0 10%;
  align-items: center;
}

.label {
  font-weight: normal;
  margin: 0;
}

.control {
  padding-left: 0;
  align-items: center;
}

.button {
  font-size: 0.75rem;
}

.cancel-btn {
  color: $primary-danger;
}

.modal-card-title {
  font-size: 1.5rem;
  font-weight: 500;
  text-align: center;
}
</style>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.pretty .state label {
  color: $grey-light; //#a6a8ab;
}
.is-event-active.pretty .state label {
  color: $black;
}
</style>
